import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './QuantumComputer.css';

type ExperimentType = 'bell' | 'ghz' | 'teleportation';

interface SimulationResult {
  counts: Record<string, number>;
  shots: number;
}

interface CircuitVisualizerProps {
  activeStep: number;
  experiment: ExperimentType;
}

function BellCircuitVisualizer({ activeStep }: { activeStep: number }) {
  return (
    <div className="circuit-visualizer">
      <div className="circuit">
        <div className="qubit-labels">
          <span>q₀</span>
          <span>q₁</span>
        </div>
        <div className="circuit-diagram">
          <div className="qubit-line">
            <div className="wire"></div>
            <div className={`gate h-gate ${activeStep === 1 ? 'active' : ''}`}>H</div>
            <div className="wire"></div>
            <div className={`control-dot ${activeStep === 2 ? 'active' : ''}`}></div>
            <div className="wire"></div>
            <div className={`meter ${activeStep === 3 ? 'active' : ''}`}>M</div>
          </div>
          <div className="qubit-line">
            <div className="wire"></div>
            <div className="empty-space"></div>
            <div className="wire"></div>
            <div className={`target-x ${activeStep === 2 ? 'active' : ''}`}></div>
            <div className="wire"></div>
            <div className={`meter ${activeStep === 3 ? 'active' : ''}`}>M</div>
          </div>
          <div className="cnot-line"></div>
        </div>
      </div>
    </div>
  );
}

function GHZCircuitVisualizer({ activeStep }: { activeStep: number }) {
  return (
    <div className="circuit-visualizer">
      <div className="circuit ghz-circuit">
        <div className="qubit-labels">
          <span>q₀</span>
          <span>q₁</span>
          <span>q₂</span>
        </div>
        <div className="circuit-diagram ghz">
          <div className="qubit-line">
            <div className="wire"></div>
            <div className={`gate h-gate ${activeStep === 1 ? 'active' : ''}`}>H</div>
            <div className="wire"></div>
            <div className={`control-dot ${activeStep === 2 ? 'active' : ''}`}></div>
            <div className="wire"></div>
            <div className={`control-dot ${activeStep === 3 ? 'active' : ''}`}></div>
            <div className="wire"></div>
            <div className={`meter ${activeStep === 4 ? 'active' : ''}`}>M</div>
          </div>
          <div className="qubit-line">
            <div className="wire"></div>
            <div className="empty-space"></div>
            <div className="wire"></div>
            <div className={`target-x ${activeStep === 2 ? 'active' : ''}`}></div>
            <div className="wire"></div>
            <div className="empty-space"></div>
            <div className="wire"></div>
            <div className={`meter ${activeStep === 4 ? 'active' : ''}`}>M</div>
          </div>
          <div className="qubit-line">
            <div className="wire"></div>
            <div className="empty-space"></div>
            <div className="wire"></div>
            <div className="empty-space"></div>
            <div className="wire"></div>
            <div className={`target-x ${activeStep === 3 ? 'active' : ''}`}></div>
            <div className="wire"></div>
            <div className={`meter ${activeStep === 4 ? 'active' : ''}`}>M</div>
          </div>
          <div className="cnot-line cnot-line-1"></div>
          <div className="cnot-line cnot-line-2"></div>
        </div>
      </div>
    </div>
  );
}

function TeleportationVisualizer({ activeStep }: { activeStep: number }) {
  return (
    <div className="teleportation-visualizer">
      <div className="teleportation-diagram">
        {/* Alice's side */}
        <div className="teleportation-side alice">
          <div className="side-label">Alice</div>
          <div className={`teleportation-qubit psi ${activeStep === 0 ? 'active' : ''}`}>
            <span>|ψ⟩</span>
          </div>
          <div className={`teleportation-qubit entangled-a ${activeStep >= 1 ? 'has-entanglement' : ''} ${activeStep === 1 ? 'active' : ''}`}>
            <span>A</span>
          </div>
          <div className={`teleportation-operation ${activeStep === 2 ? 'active' : ''}`}>
            <span>CNOT + H</span>
          </div>
          <div className={`teleportation-measurement ${activeStep === 3 ? 'active' : ''}`}>
            <span>M</span>
            <span className="bits">→ 2 bits</span>
          </div>
        </div>

        {/* Entanglement connection */}
        <div className={`entanglement-line ${activeStep >= 1 ? 'visible' : ''}`}>
          <svg viewBox="0 0 100 60" preserveAspectRatio="none">
            <path d="M0,30 Q50,0 100,30" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="5,3"/>
          </svg>
        </div>

        {/* Classical channel */}
        <div className={`classical-channel ${activeStep >= 4 ? 'active' : ''}`}>
          <div className="channel-label">Classical</div>
          <div className="channel-arrow">
            <span className="bits-sent">{activeStep >= 4 ? '01' : ''}</span>
            →
          </div>
        </div>

        {/* Bob's side */}
        <div className="teleportation-side bob">
          <div className="side-label">Bob</div>
          <div className={`teleportation-qubit entangled-b ${activeStep >= 1 ? 'has-entanglement' : ''} ${activeStep === 1 ? 'active' : ''}`}>
            <span>B</span>
          </div>
          <div className={`teleportation-operation correction ${activeStep === 5 ? 'active' : ''}`}>
            <span>X/Z</span>
          </div>
          <div className={`teleportation-qubit result ${activeStep === 5 ? 'active success' : ''}`}>
            <span>|ψ⟩</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CircuitVisualizer({ activeStep, experiment }: CircuitVisualizerProps) {
  if (experiment === 'teleportation') {
    return <TeleportationVisualizer activeStep={activeStep} />;
  }
  if (experiment === 'ghz') {
    return <GHZCircuitVisualizer activeStep={activeStep} />;
  }
  return <BellCircuitVisualizer activeStep={activeStep} />;
}

interface ResultsChartProps {
  counts: Record<string, number>;
  shots: number;
  experiment: ExperimentType;
}

function ResultsChart({ counts, shots, experiment }: ResultsChartProps) {
  const states = experiment === 'ghz'
    ? ['000', '001', '010', '011', '100', '101', '110', '111']
    : ['00', '01', '10', '11'];

  return (
    <div className={`results-chart ${experiment === 'ghz' ? 'ghz-chart' : ''}`}>
      {states.map((state) => {
        const count = counts[state] || 0;
        const percentage = ((count / shots) * 100).toFixed(1);
        const height = (count / shots) * 100;

        return (
          <div key={state} className="bar-container">
            <div className="bar-wrapper">
              <div
                className="bar"
                style={{ height: `${height}%` }}
              >
                <span className="bar-value">{percentage}%</span>
              </div>
            </div>
            <span className="bar-label">|{state}⟩</span>
            <span className="bar-count">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function QuantumComputer() {
  const { t } = useTranslation();
  const [experiment, setExperiment] = useState<ExperimentType>('bell');
  const [results, setResults] = useState<SimulationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [animationStep, setAnimationStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const totalSteps = experiment === 'teleportation' ? 6 : experiment === 'ghz' ? 5 : 4;

  const handleExperimentChange = useCallback((newExperiment: ExperimentType) => {
    setExperiment(newExperiment);
    setResults(null);
    setAnimationStep(0);
    setIsPlaying(false);
  }, []);

  const nextStep = useCallback(() => {
    setAnimationStep(prev => Math.min(prev + 1, totalSteps - 1));
  }, [totalSteps]);

  const prevStep = useCallback(() => {
    setAnimationStep(prev => Math.max(prev - 1, 0));
  }, []);

  const togglePlay = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  // Auto-play animation
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setAnimationStep(prev => {
        if (prev >= totalSteps - 1) {
          setIsPlaying(false);
          return 0;
        }
        return prev + 1;
      });
    }, 1500);

    return () => clearInterval(interval);
  }, [isPlaying, totalSteps]);

  const runSimulation = async () => {
    setLoading(true);
    setError(null);

    const endpoint = experiment === 'ghz' ? '/api/ghz-state' : '/api/bell-state';

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shots: 1024 })
      });

      if (!response.ok) {
        throw new Error('API request failed');
      }

      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError(t('quantumComputer.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="quantum-computer-page">
      <h1 className="page-title">{t('quantumComputer.title')}</h1>
      <p className="page-intro">{t('quantumComputer.intro')}</p>

      {/* Experiment Selector */}
      <div className="experiment-selector">
        <button
          className={experiment === 'bell' ? 'active' : ''}
          onClick={() => handleExperimentChange('bell')}
        >
          <span className="experiment-name">{t('quantumComputer.experiments.bell.title')}</span>
          <span className="experiment-desc">{t('quantumComputer.experiments.bell.description')}</span>
        </button>
        <button
          className={experiment === 'ghz' ? 'active' : ''}
          onClick={() => handleExperimentChange('ghz')}
        >
          <span className="experiment-name">{t('quantumComputer.experiments.ghz.title')}</span>
          <span className="experiment-desc">{t('quantumComputer.experiments.ghz.description')}</span>
        </button>
        <button
          className={experiment === 'teleportation' ? 'active' : ''}
          onClick={() => handleExperimentChange('teleportation')}
        >
          <span className="experiment-name">{t('quantumComputer.experiments.teleportation.title')}</span>
          <span className="experiment-desc">{t('quantumComputer.experiments.teleportation.description')}</span>
        </button>
      </div>

      <section className="circuit-section">
        <h2>{t('quantumComputer.circuitTitle')}</h2>
        <CircuitVisualizer activeStep={animationStep} experiment={experiment} />
        <p className="circuit-explanation">
          {experiment === 'teleportation'
            ? t('quantumComputer.teleportationCircuitExplanation')
            : experiment === 'ghz'
              ? t('quantumComputer.ghzCircuitExplanation')
              : t('quantumComputer.circuitExplanation')}
        </p>
        {experiment === 'teleportation' && (
          <p className="teleportation-note">{t('quantumComputer.teleportationNote')}</p>
        )}

        {/* Animation Controls */}
        <div className="animation-section">
          <h3>{t('quantumComputer.animation.title')}</h3>

          <div className="state-display">
            <div className="math-state">
              {experiment === 'teleportation'
                ? t(`quantumComputer.teleportationAnimation.states.state${animationStep}`)
                : experiment === 'ghz'
                  ? t(`quantumComputer.ghzAnimation.states.state${animationStep}`)
                  : t(`quantumComputer.animation.states.state${animationStep}`)}
            </div>
            <p className="step-description">
              {experiment === 'teleportation'
                ? t(`quantumComputer.teleportationAnimation.steps.step${animationStep}`)
                : experiment === 'ghz'
                  ? t(`quantumComputer.ghzAnimation.steps.step${animationStep}`)
                  : t(`quantumComputer.animation.steps.step${animationStep}`)}
            </p>
          </div>

          <div className="animation-controls">
            <button onClick={prevStep} disabled={animationStep === 0 || isPlaying}>
              {t('quantumComputer.animation.prev')}
            </button>
            <span className="step-indicator">
              {t('quantumComputer.animation.step')} {animationStep + 1} {t('quantumComputer.animation.of')} {totalSteps}
            </span>
            <button onClick={nextStep} disabled={animationStep === totalSteps - 1 || isPlaying}>
              {t('quantumComputer.animation.next')}
            </button>
            <button className="play-btn" onClick={togglePlay}>
              {isPlaying ? t('quantumComputer.animation.pause') : t('quantumComputer.animation.play')}
            </button>
          </div>
        </div>
      </section>

      {experiment !== 'teleportation' && (
        <>
          <div className="simulation-controls">
            <button
              className="run-button"
              onClick={runSimulation}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  {t('quantumComputer.running')}
                </>
              ) : (
                t('quantumComputer.runButton')
              )}
            </button>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {results && (
            <section className="results-section">
              <h2>{t('quantumComputer.resultsTitle')}</h2>
              <ResultsChart counts={results.counts} shots={results.shots} experiment={experiment} />
              <p className="results-explanation">
                {experiment === 'ghz'
                  ? t('quantumComputer.ghzResultsExplanation')
                  : t('quantumComputer.resultsExplanation')}
              </p>
            </section>
          )}
        </>
      )}

      <section className="educational-section">
        <h2>{t('quantumComputer.whatIsEntanglement')}</h2>
        <p>{t('quantumComputer.entanglementExplanation')}</p>
      </section>

      {/* Deep Dive Section */}
      <section className="deep-dive-section">
        <h2 className="section-title">{t('quantumComputer.deepDive.title')}</h2>
        <p className="section-intro">{t('quantumComputer.deepDive.intro')}</p>

        <div className="deep-dive-grid">
          <div className="deep-dive-card">
            <div className="deep-dive-icon" aria-hidden="true">
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="16" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.1"/>
                <circle cx="24" cy="24" r="8" stroke="currentColor" strokeWidth="2" strokeDasharray="4 2"/>
                <circle cx="24" cy="24" r="3" fill="currentColor"/>
                <path d="M24 8 L24 4" stroke="currentColor" strokeWidth="2"/>
                <path d="M24 44 L24 40" stroke="currentColor" strokeWidth="2"/>
                <path d="M8 24 L4 24" stroke="currentColor" strokeWidth="2"/>
                <path d="M44 24 L40 24" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <h3>{t('quantumComputer.deepDive.qubit.title')}</h3>
            <p>{t('quantumComputer.deepDive.qubit.text')}</p>
          </div>

          <div className="deep-dive-card">
            <div className="deep-dive-icon" aria-hidden="true">
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="14" y="14" width="20" height="20" rx="4" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.2"/>
                <text x="24" y="29" fontSize="14" fill="currentColor" textAnchor="middle" fontWeight="bold">H</text>
                <path d="M4 24 L14 24" stroke="currentColor" strokeWidth="2"/>
                <path d="M34 24 L44 24" stroke="currentColor" strokeWidth="2"/>
                <circle cx="8" cy="24" r="3" fill="currentColor" fillOpacity="0.5"/>
                <path d="M38 20 L42 24 L38 28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
              </svg>
            </div>
            <h3>{t('quantumComputer.deepDive.hadamard.title')}</h3>
            <p>{t('quantumComputer.deepDive.hadamard.text')}</p>
          </div>

          <div className="deep-dive-card">
            <div className="deep-dive-icon" aria-hidden="true">
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="14" r="5" fill="currentColor"/>
                <path d="M24 19 L24 29" stroke="currentColor" strokeWidth="2"/>
                <circle cx="24" cy="34" r="8" stroke="currentColor" strokeWidth="2" fill="none"/>
                <path d="M24 26 L24 42" stroke="currentColor" strokeWidth="2"/>
                <path d="M16 34 L32 34" stroke="currentColor" strokeWidth="2"/>
                <path d="M4 14 L19 14" stroke="currentColor" strokeWidth="2" opacity="0.5"/>
                <path d="M29 14 L44 14" stroke="currentColor" strokeWidth="2" opacity="0.5"/>
                <path d="M4 34 L16 34" stroke="currentColor" strokeWidth="2" opacity="0.5"/>
                <path d="M32 34 L44 34" stroke="currentColor" strokeWidth="2" opacity="0.5"/>
              </svg>
            </div>
            <h3>{t('quantumComputer.deepDive.cnot.title')}</h3>
            <p>{t('quantumComputer.deepDive.cnot.text')}</p>
          </div>

          <div className="deep-dive-card highlight">
            <div className="deep-dive-icon" aria-hidden="true">
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M24 4 L24 18" stroke="currentColor" strokeWidth="2"/>
                <circle cx="24" cy="28" r="14" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.1"/>
                <path d="M24 28 L24 18" stroke="currentColor" strokeWidth="2"/>
                <path d="M24 28 L32 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M14 28 L34 28" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
                <circle cx="24" cy="28" r="2" fill="currentColor"/>
              </svg>
            </div>
            <h3>{t('quantumComputer.deepDive.measurement.title')}</h3>
            <p>{t('quantumComputer.deepDive.measurement.text')}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
